'use client';

import { useState, useEffect, useRef } from 'react';
import { isoToDisplay, displayToIso, maskNzDateInput } from '@shared/nz-date';
import { FieldError } from './FieldError';
import styles from './DateInputNZ.module.css';

// NZ-format date input. Displays DD/MM/YYYY, stores ISO YYYY-MM-DD.
// Native <input type="date"> follows the *browser locale*, which puts US users
// (and anyone with a US-locale browser) on MM/DD/YYYY — that's the bug we're
// fixing. This component is the single seam: every callsite that used to be
// <input type="date" /> goes through here so display + parse stay in lockstep.

interface DateInputNZProps {
  /** ISO YYYY-MM-DD, or empty string when unset. */
  value: string;
  /** Called with ISO YYYY-MM-DD or empty string. Not called while the user is
   *  mid-edit with an unparseable string — only on blur or successful parse. */
  onChange: (iso: string) => void;
  /** Optional minimum bound, ISO YYYY-MM-DD. */
  min?: string;
  placeholder?: string;
  hasError?: boolean;
  /** Authoritative message from the parent's error map (empty + required).
   *  Rendered as the inline alert when present; an internal parse error
   *  (unparseable text the validator never sees) takes priority. */
  error?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  ariaLabel?: string;
  onBlur?: () => void;
  /** Called when the parse-validity of the field changes. Lets the parent
   *  gate Continue / Save against unparseable input even though the
   *  component itself swallows invalid text and never updates `value`. */
  onValidityChange?: (valid: boolean) => void;
}

export function DateInputNZ({
  value,
  onChange,
  min,
  placeholder = 'DD/MM/YYYY',
  hasError,
  error,
  required,
  disabled,
  id,
  name,
  ariaLabel,
  onBlur,
  onValidityChange,
}: DateInputNZProps) {
  const [draft, setDraft] = useState<string>(() => isoToDisplay(value));
  const [parseError, setParseError] = useState(false);
  const focusedRef = useRef(false);

  // Reflect external value changes, but never overwrite mid-edit.
  useEffect(() => {
    if (focusedRef.current) return;
    setDraft(isoToDisplay(value));
    setParseError(false);
  }, [value]);

  const commit = (text: string) => {
    const iso = displayToIso(text);
    if (iso === null) {
      setParseError(true);
      onValidityChange?.(false);
      return;
    }
    setParseError(false);
    onValidityChange?.(true);
    // Canonicalise the visible draft to DD/MM/YYYY on every successful parse,
    // even when the ISO is unchanged. Without this, shorthand input like
    // "7/3/27" stays in the field after blur because the [value] effect
    // doesn't fire when value didn't change.
    setDraft(isoToDisplay(iso));
    if (iso !== value) onChange(iso);
  };

  const handleBlur = () => {
    focusedRef.current = false;
    commit(draft);
    onBlur?.();
  };

  const showError = hasError || parseError || Boolean(error);
  // Parse error owns the message (the validator can't see unparseable text);
  // otherwise fall back to the map message from the parent.
  const message = parseError ? (error ?? 'Enter a valid date (DD/MM/YYYY)') : error;

  return (
    <>
      <input
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        aria-label={ariaLabel}
        aria-invalid={showError || undefined}
        data-min={min || undefined}
        onFocus={() => { focusedRef.current = true; }}
        onChange={(e) => {
          setDraft(maskNzDateInput(e.target.value));
          setParseError(false);
        }}
        onBlur={handleBlur}
        className={`${styles.input} ${showError ? styles.invalid : ''}`}
      />
      <FieldError message={message} />
    </>
  );
}
